<!--
  Clienti — lista.

  · i dati si caricano A BATCH dal server (25 righe per volta): il backend
    tronca a 1000 record senza segnalarlo;
  · il segmento "in conferma" parte da /minute con i join car+car.customer,
    in una sola chiamata;
  · ricerca e filtri sono applicati dal database.
-->
<template>
  <div class="cst">
    <!-- Intestazione -->
    <div class="cst__head">
      <div>
        <h1 class="cst__title">
          Clienti
        </h1>
        <p class="cst__sub">
          Anagrafica clienti privati, aziende e tutti i pacchetti valet.
        </p>
      </div>
      <div class="cst__headActions">
        <el-input
          v-model="search"
          class="cst__search"
          placeholder="Cerca nome, mail, telefono, città…"
          clearable
          @input="onSearchInput"
        />
        <router-link
          :to="{ path: '/customer/add-customer', query: { q: 1 } }"
          class="routerLink"
        >
          <el-button type="primary">
            Nuovo cliente
          </el-button>
        </router-link>
      </div>
    </div>

    <!-- KPI: sono anche il selettore di segmento -->
    <div class="cst__kpis">
      <button
        v-for="k in kpis"
        :key="k.key"
        class="cst__kpi"
        :class="{ 'is-active': segment === k.key, 'is-attn': k.attn }"
        type="button"
        @click="setSegment(k.key)"
      >
        <span class="cst__kpiNum">
          {{ k.value === null ? '—' : k.value }}
          <!-- Il numero grande è QUANTI SONO; quello piccolo quanti chiedono
               un'azione. Contare solo questi ultimi faceva leggere «0 valet»
               quando erano tutti confermati. -->
          <small
            v-if="k.badge"
            class="cst__kpiBadge"
          >{{ k.badge }}</small>
        </span>
        <span class="cst__kpiLbl">{{ k.label }}</span>
      </button>
    </div>

    <!-- Tabella -->
    <div class="cst__card">
      <el-table
        v-loading="loading"
        :data="rows"
        style="width: 100%"
        empty-text="Nessun cliente corrisponde ai filtri"
        @row-click="openDetail"
        @sort-change="onSortChange"
      >
        <!-- ---- CLIENTI / AZIENDE ---- -->
        <template v-if="segment !== 'conferma'">
          <!-- Nessun ordinamento su questa colonna: l'elenco è GIÀ ordinato
               per cognome (o ragione sociale) di default, quindi il click
               "crescente" non cambierebbe nulla e sembrerebbe rotto. In più
               qui si legge "Nome Cognome" mentre l'ordine è per cognome:
               l'ordinamento risulterebbe incomprensibile a chi guarda. -->
          <el-table-column
            :label="segment === 'aziende' ? 'Azienda' : 'Cliente'"
            min-width="240"
          >
            <template #default="scope">
              <div class="cst__who">
                <span class="cst__avatar">{{ initials(scope.row) }}</span>
                <div>
                  <div class="cst__name">
                    {{ displayName(scope.row) }}
                  </div>
                  <div class="cst__meta">
                    {{ scope.row.mail }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            v-if="segment === 'aziende'"
            label="Referente"
            min-width="160"
          >
            <template #default="scope">
              {{ [scope.row.name, scope.row.surname].filter(Boolean).join(' ') || '—' }}
            </template>
          </el-table-column>

          <el-table-column
            prop="telephone_number"
            label="Telefono"
            min-width="150"
          >
            <template #default="scope">
              <span class="cst__num">{{ scope.row.telephone_number || '—' }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Città"
            prop="city"
            sortable="custom"
            min-width="140"
          >
            <template #default="scope">
              {{ scope.row.city || '—' }}
            </template>
          </el-table-column>

          <el-table-column
            v-if="segment === 'aziende'"
            label="P. IVA"
            min-width="140"
          >
            <template #default="scope">
              <span class="cst__num">{{ scope.row.piva || '—' }}</span>
            </template>
          </el-table-column>
        </template>

        <!-- ---- IN CONFERMA (righe da /minute) ---- -->
        <template v-else>
          <el-table-column
            label="Cliente"
            min-width="240"
          >
            <template #default="scope">
              <div class="cst__who">
                <span class="cst__avatar">{{ initials(scope.row._customer) }}</span>
                <div>
                  <div class="cst__name">
                    {{ displayName(scope.row._customer) }}
                  </div>
                  <div class="cst__meta">
                    {{ scope.row._customer.mail }}
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Auto"
            min-width="170"
          >
            <template #default="scope">
              <div>{{ scope.row._carLabel || '—' }}</div>
              <div
                v-if="scope.row.car && scope.row.car.targa"
                class="cst__plate"
              >
                {{ scope.row.car.targa }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Valet"
            align="right"
            width="110"
          >
            <template #default="scope">
              <span class="cst__num cst__strong">{{ scope.row.valet }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Imponibile"
            align="right"
            width="140"
          >
            <template #default="scope">
              <span class="cst__num">{{ euro(scope.row.imponibile) }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Richiesto il"
            width="140"
          >
            <template #default="scope">
              <span class="cst__num">{{ shortDate(scope.row.data_reg) }}</span>
            </template>
          </el-table-column>
        </template>

        <!-- ---- Stato + azioni (comuni) ----
             Nei segmenti clienti/aziende sarebbero tutti "Attivo" (il filtro
             esclude già i non attivi): una colonna sempre uguale è rumore.
             Qui la pill compare SOLO se c'è qualcosa da sapere, cioè un valet
             in attesa di conferma per quel cliente. -->
        <el-table-column
          label="Stato"
          width="170"
        >
          <template #default="scope">
            <!-- La scheda Valet mostra tutti i pacchetti: la pill dice se
                 quello è confermato o ancora in attesa. -->
            <StatusPill
              v-if="segment === 'conferma'"
              :kind="scope.row.accepted === true ? 'ok' : 'attn'"
              :label="scope.row.accepted === true ? 'Confermato' : 'Da confermare'"
            />
            <StatusPill
              v-else-if="pendingValetIds.has(scope.row.id)"
              kind="attn"
              label="Valet da confermare"
            />
            <span
              v-else
              class="cst__dash"
            >—</span>
          </template>
        </el-table-column>

        <el-table-column
          width="130"
          align="right"
          label="Azioni"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              type="primary"
              size="small"
              @click.stop="openDetail(scope.row)"
            >
              Dettagli
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Paginazione: ogni cambio pagina è una nuova richiesta al server -->
      <div class="cst__foot">
        <span class="cst__footInfo">
          {{ total }} {{ segment === 'conferma' ? 'pacchetti valet' : (segment === 'aziende' ? 'aziende' : 'clienti') }}
          <template v-if="total > pageSize">· pagina {{ page }} di {{ pageCount }}</template>
        </span>
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="total"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import customerAPI from '../../services/customerAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';

const PAGE_SIZE = 25;

export default {
  name: 'CustomerPage',
  components: { StatusPill },

  setup() {
    const router = useRouter();

    const segment = ref('clienti'); // clienti | aziende | conferma
    const rows = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const total = ref(0);
    const pageCount = ref(1);
    const search = ref('');
    const sort = ref(''); // "campo,ASC|DESC" — ordinamento applicato dal DB
    const pendingValetIds = ref(new Set());

    // Conteggi dei KPI: caricati una volta, servono anche a chi non apre
    // il segmento (es. sapere che ci sono valet in attesa).
    const countClienti = ref(null);
    const countAziende = ref(null);
    const countConferma = ref(null);
    const countValet = ref(null);

    const kpis = computed(() => [
      { key: 'clienti', label: 'Clienti', value: countClienti.value },
      { key: 'aziende', label: 'Aziende', value: countAziende.value },
      // Tutti i pacchetti, dal più recente; accanto, piccoli, quanti sono
      // ancora da confermare.
      {
        key: 'conferma',
        label: 'Valet',
        value: countValet.value,
        badge: countConferma.value ? `${countConferma.value} da confermare` : '',
        attn: !!countConferma.value,
      },
    ]);

    // Carica la pagina corrente del segmento selezionato.
    const load = async () => {
      loading.value = true;
      try {
        const res =
          segment.value === 'conferma'
            ? await customerAPI.getPendingValetPaged({ page: page.value, limit: PAGE_SIZE })
            : await customerAPI.getCustomersPaged({
              page: page.value,
              limit: PAGE_SIZE,
              segment: segment.value,
              search: search.value,
              sort: sort.value,
            });

        rows.value =
          segment.value === 'conferma'
            ? res.rows.map(decorateValetRow)
            : res.rows;
        total.value = res.total;
        pageCount.value = res.pageCount || 1;
      } catch (e) {
        console.error('Caricamento clienti fallito:', e);
        rows.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    // Le righe "in conferma" arrivano da /minute: il cliente sta sotto
    // car.customer. Lo appiattiamo per non sporcare il template.
    const decorateValetRow = (m) => ({
      ...m,
      _customer: m.car?.customer || {},
      _carLabel: [m.car?.marca, m.car?.modello].filter(Boolean).join(' '),
    });

    const loadCounts = async () => {
      const [cli, az, conf, tot, ids] = await Promise.allSettled([
        customerAPI.countSegment('clienti'),
        customerAPI.countSegment('aziende'),
        customerAPI.countPendingValet(),
        customerAPI.countPendingValet({ soloDaConfermare: false }),
        customerAPI.getPendingValetCustomerIds(),
      ]);
      if (cli.status === 'fulfilled') countClienti.value = cli.value;
      if (az.status === 'fulfilled') countAziende.value = az.value;
      if (conf.status === 'fulfilled') countConferma.value = conf.value;
      if (tot.status === 'fulfilled') countValet.value = tot.value;
      if (ids.status === 'fulfilled') pendingValetIds.value = ids.value;
    };

    const setSegment = (key) => {
      if (segment.value === key) return;
      segment.value = key;
      page.value = 1;
      sort.value = ''; // ogni segmento riparte dal suo ordinamento naturale
      load();
    };

    // Ordinamento: si delega al server e si torna a pagina 1, così l'ordine
    // vale su TUTTI i clienti e non solo sulle 25 righe già scaricate.
    const onSortChange = ({ prop, order }) => {
      sort.value = prop && order ? `${prop},${order === 'ascending' ? 'ASC' : 'DESC'}` : '';
      page.value = 1;
      load();
    };

    const onPageChange = (p) => {
      page.value = p;
      load();
    };

    // Debounce: si aspetta che l'utente smetta di digitare prima di
    // interrogare il server.
    let searchTimer = null;
    const onSearchInput = () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        page.value = 1;
        load();
      }, 350);
    };

    const openDetail = (row) => {
      const id = segment.value === 'conferma' ? row._customer?.id : row.id;
      if (id) router.push(`/customer/detail/${id}`);
    };

    // ---- helper di presentazione -------------------------------------
    const displayName = (c) =>
      c?.company_name || [c?.name, c?.surname].filter(Boolean).join(' ') || '—';

    const initials = (c) => {
      const src = c?.company_name || `${c?.name || ''} ${c?.surname || ''}`;
      return (
        src
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map((w) => w[0])
          .join('')
          .toUpperCase() || '?'
      );
    };

    const euro = (v) =>
      v === null || v === undefined || v === ''
        ? '—'
        : `€ ${Number(v).toLocaleString('it-IT', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

    const shortDate = (v) =>
      v ? new Date(v).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—';

    onMounted(() => {
      load();
      loadCounts();
    });

    return {
      segment, rows, loading, page, total, pageCount, search, pendingValetIds,
      pageSize: PAGE_SIZE, kpis,
      setSegment, onPageChange, onSearchInput, onSortChange, openDetail,
      displayName, initials, euro, shortDate,
    };
  },
};
</script>

<style scoped>
.cst {
  padding: 4px 0 32px;
}

/* ---- intestazione ---- */
.cst__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.cst__title {
  margin: 0;
  font-size: 22px;
  color: var(--text-primary);
}
.cst__sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--text-tertiary);
}
.cst__headActions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.cst__search {
  width: 280px;
}

/* ---- KPI cliccabili (fanno anche da selettore di segmento) ---- */
.cst__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 720px) {
  .cst__kpis { grid-template-columns: 1fr; }
}
.cst__kpi {
  text-align: left;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 13px 16px;
  cursor: pointer;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
  font-family: inherit;
}
.cst__kpi:hover { border-color: var(--brand-violet); }
.cst__kpi.is-active {
  border-color: var(--brand-violet);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.cst__kpiNum {
  display: block;
  font-size: 24px;
  font-weight: 750;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--text-primary);
}
.cst__kpiBadge {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--state-attn);
  white-space: nowrap;
}
.cst__kpi.is-attn .cst__kpiNum { color: var(--state-attn); }
.cst__kpiLbl {
  display: block;
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

/* ---- tabella ---- */
.cst__card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.cst__who {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cst__avatar {
  width: 32px;
  height: 32px;
  flex: none;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--avatar-bg);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}
.cst__name { font-weight: 600; color: var(--text-primary); }
.cst__meta { font-size: 12px; color: var(--text-tertiary); }
.cst__num { font-variant-numeric: tabular-nums; }
.cst__strong { font-weight: 700; }
.cst__plate {
  display: inline-block;
  margin-top: 3px;
  padding: 0 5px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-tertiary);
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: var(--text-secondary);
}

/* ---- piede / paginazione ---- */
.cst__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.cst__footInfo {
  font-size: 12.5px;
  color: var(--text-tertiary);
}

/* la riga è cliccabile: lo si deve capire */
.cst :deep(.el-table__row) { cursor: pointer; }
.cst__dash { color: var(--text-tertiary); }

/* ---- intestazione tabella in stile "scheda" ----
   element-overrides.css impone a TUTTE le tabelle un header viola in
   gradiente con !important. Su una lista densa pesa e ruba attenzione ai
   dati: qui lo riportiamo al grigio chiaro dei mockup. Serve !important per
   vincere sull'override globale. */
.cst :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 11px 12px;
  border-bottom: 1px solid var(--border-color);
}
/* frecce di ordinamento leggibili sul fondo chiaro */
.cst :deep(.el-table .caret-wrapper) { vertical-align: middle; }
.cst :deep(.el-table th.el-table__cell.is-sortable:hover) {
  color: var(--text-primary) !important;
}
</style>
